import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { X, PhoneCall } from "lucide-react";
import { useT, tr } from "@/lib/i18n";

export const TalkToEngineerModal = ({ open, onClose, defaults = {}, projectId, source = "results" }) => {
  const [form, setForm] = useState({
    name: defaults.name || "",
    company: defaults.company || "",
    email: defaults.email || "",
    country: defaults.country || "",
    project_summary: defaults.summary || "",
  });
  const [busy, setBusy] = useState(false);
  useT();

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.email || !form.company) {
      toast.error(tr("Please fill name, company and email", "Veuillez renseigner le nom, l'entreprise et l'e-mail"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", { ...form, project_id: projectId, source });
      track("talk_to_engineer_click", { source, country: form.country });
      toast.success(source === "enterprise"
        ? tr("Received. An account manager will reach out within one business day.", "Bien reçu. Un responsable de compte vous contactera sous un jour ouvré.")
        : tr("Received. An engineer will reach out within one business day.", "Bien reçu. Un ingénieur vous contactera sous un jour ouvré."));
      onClose();
    } catch {
      toast.error(tr("Could not submit — please try again", "Envoi impossible — veuillez réessayer"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(22,33,29,0.55)" }} data-testid="talk-modal">
      <div className="card p-8 w-[95%] max-w-lg relative" style={{ background: "var(--white)" }}>
        <button onClick={onClose} className="absolute top-4 right-4" style={{ color: "var(--muted)" }} data-testid="talk-modal-close">
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <PhoneCall size={18} style={{ color: "var(--brand-green)" }} />
          <div className="overline">{source === "enterprise" ? tr("Enterprise & custom bundles", "Entreprise et packs sur mesure") : tr("Extended workflow", "Accompagnement étendu")}</div>
        </div>
        <h3 className="font-display text-2xl mb-2">{source === "enterprise" ? tr("Talk to an account manager", "Parler à un responsable de compte") : tr("Contact our team", "Contacter notre équipe")}</h3>
        <p className="text-sm mb-6" style={{ color: "var(--muted)" }}>
          {source === "enterprise"
            ? tr("Discuss annual plans, custom credit bundles, seat pricing and volume discounts. Typical response within one business day.", "Échangez sur les abonnements annuels, les packs de crédits sur mesure, la tarification par utilisateur et les remises sur volume. Réponse habituelle sous un jour ouvré.")
            : tr("Have a specific need beyond the automated report? Send us a note and we'll respond within one business day. Report credits count towards any follow-up engagement.", "Vous avez un besoin précis au-delà du rapport automatisé\u00a0? Écrivez-nous et nous vous répondrons sous un jour ouvré. Les crédits de rapport sont déduits de toute mission ultérieure.")}
        </p>
        <div className="grid grid-cols-1 gap-4">
          <Field label={tr("Full name", "Nom complet")} value={form.name} onChange={(v) => set("name", v)} testid="lead-name" />
          <Field label={tr("Company", "Entreprise")} value={form.company} onChange={(v) => set("company", v)} testid="lead-company" />
          <div className="grid grid-cols-2 gap-4">
            <Field label={tr("Work email", "E-mail professionnel")} type="email" value={form.email} onChange={(v) => set("email", v)} testid="lead-email" />
            <Field label={tr("Country", "Pays")} value={form.country} onChange={(v) => set("country", v)} testid="lead-country" />
          </div>
          <div>
            <label className="label-field">{tr("Project summary", "Résumé du projet")}</label>
            <textarea
              className="field-input"
              rows={3}
              value={form.project_summary}
              onChange={(e) => set("project_summary", e.target.value)}
              data-testid="lead-summary"
            />
          </div>
        </div>
        <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center mt-6" data-testid="lead-submit">
          {busy ? tr("Sending…", "Envoi…") : (source === "enterprise" ? tr("Request account manager callback", "Demander à être rappelé par un responsable de compte") : tr("Send message", "Envoyer le message"))}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="label-field">{label}</label>
    <input className="field-input" type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
